import * as React from 'react';
import Flexbox from 'components/UI/FlexboxContainer';
import { Heading } from './UpsertServiceForm';
import { Size } from 'lib/styles';
import styled from 'styled-components';
import { DEFAULT_UOM_CODE, SERVICE_CONSTANTS, SERVICE_FORM_ERRORS, SERVICE_FORM_FIELDS, SERVICE_FORM_RULES } from 'constants/service';
import InfoIcon from 'components/UI/InfoIcon';
import { Control, Controller, UseFormGetValues, UseFormSetValue } from 'react-hook-form';
import { IFormData } from 'data/Service';
import { renderCheckbox, renderCurrencyFormatter } from '../UI/RenderFields';
import { GENERAL_CONSTANTS, STORAGE_KEYS } from 'constants/general';
import { CURRENCY_ISO } from 'constants/country';
import InputLabel from 'components/UI/Label';
import SelectComponent from 'components/UI/Select';
import { getItems } from 'lib/SessionStorage';
import { UnitOfMeasurement } from 'data/UnitOfMeasurement';
import { Main } from 'styles/themeColors';
import { PermissionCode } from 'data/UserPermissions';
import { useServicePermissions } from 'hooks/useServicePermissions';

const StyledInfoIcon = styled(InfoIcon)`
  display: flex;
  margin-left: ${Size.small};
  margin-top: 0.1rem;
  height: 24px;
`;

const Row = styled(Flexbox)`
    margin-top: ${Size.small};
    width: 100%;
`;

interface Props {
    control: Control<IFormData>;
    errors: any;
    watch: any;
    setError?: any;
    clearErrors?: any;
    getValues: UseFormGetValues<IFormData>;
    setValue?: UseFormSetValue<IFormData>;
}

const ServiceCountries: React.FC<Props> = ({ control, errors, watch, clearErrors, getValues, setValue }: Props) => {
    const hasUSA = watch(SERVICE_FORM_FIELDS.IS_FOR_USA);
    const hasCanada = watch(SERVICE_FORM_FIELDS.IS_FOR_CANADA);
    const unitOfMeasurementCaId = watch(SERVICE_FORM_FIELDS.MEASUREMENT_UNIT_CA);
    const unitOfMeasurementUsaId = watch(SERVICE_FORM_FIELDS.MEASUREMENT_UNIT_USA);

    // Track previous state of country checkboxes to detect changes
    const prevHasUSA = React.useRef(hasUSA);
    const prevHasCanada = React.useRef(hasCanada);

    const unitOfMeasurements: UnitOfMeasurement[] = getItems(STORAGE_KEYS.UNIT_OF_MEASUREMENTS) || [];

    // Find UOM with externalCode 'day'
    const defaultDayUOM = React.useMemo(() => {
        const dayUOM: UnitOfMeasurement = unitOfMeasurements.find(uom => uom.externalCode === DEFAULT_UOM_CODE)!;
        return dayUOM;
    }, [unitOfMeasurements]);

    const noCountrySelected = errors?.isForCanada?.message && errors?.isForUSA?.message ? SERVICE_FORM_ERRORS.COUNTRY : '';

    const unitOfMeasurementCa = unitOfMeasurements.find((ct) => ct.id === unitOfMeasurementCaId);
    const unitOfMeasurementUsa = unitOfMeasurements.find((ct) => ct.id === unitOfMeasurementUsaId);

    // Set default UOM when component mounts or when dependencies change
    React.useEffect(() => {
        // When Canada is selected and no UOM is set, set default UOM
        if (hasCanada && !unitOfMeasurementCaId && defaultDayUOM) {
            setValue?.(SERVICE_FORM_FIELDS.MEASUREMENT_UNIT_CA, defaultDayUOM.id);
        }

        // When USA is selected and no UOM is set, set default UOM
        if (hasUSA && !unitOfMeasurementUsaId && defaultDayUOM) {
            setValue?.(SERVICE_FORM_FIELDS.MEASUREMENT_UNIT_USA, defaultDayUOM.id);
        }

        // Reset Canada UOM when checkbox is unchecked
        if (prevHasCanada.current && !hasCanada) {
            setValue?.(SERVICE_FORM_FIELDS.MEASUREMENT_UNIT_CA, null);
            setValue?.(SERVICE_FORM_FIELDS.CANADA_PRICE, null);
        }

        // Reset USA UOM when checkbox is unchecked
        if (prevHasUSA.current && !hasUSA) {
            setValue?.(SERVICE_FORM_FIELDS.MEASUREMENT_UNIT_USA, null);
            setValue?.(SERVICE_FORM_FIELDS.USA_PRICE, null);
        }

        // Update refs to current values for next comparison
        prevHasCanada.current = hasCanada;
        prevHasUSA.current = hasUSA;

    }, [hasCanada, hasUSA, defaultDayUOM, unitOfMeasurementCaId, unitOfMeasurementUsaId, setValue]);


    const { isCreateMode, readOnly } = useServicePermissions();

    return (
        <>
            <Flexbox alignItems='center'>
                <Heading>{SERVICE_CONSTANTS.SELECT_SERVICE_COUNTRY}</Heading>
                <StyledInfoIcon
                    productRedesignFlag
                    tooltipText={SERVICE_CONSTANTS.SERVICE_COUNTRY_TOOLTIP}
                    size={14}
                />
            </Flexbox>

            {/* Canada Section */}
            <Flexbox width={'60%'} flexDirection='row' gap='10px'>
                <Flexbox flexDirection='column' width='35%'>
                    <div style={{ paddingTop: 40 }}>
                        <Controller
                            name={SERVICE_FORM_FIELDS.IS_FOR_CANADA}
                            control={control}
                            rules={SERVICE_FORM_RULES.IS_FOR_CANADA(clearErrors)}
                            render={({ field }) =>
                                renderCheckbox(field,
                                    SERVICE_FORM_FIELDS.IS_FOR_CANADA,
                                    GENERAL_CONSTANTS.CANADA,
                                    '',
                                    isCreateMode ? PermissionCode.ADDONS_CREATE : PermissionCode.EDIT_ADDON_DETAILS
                                )
                            }
                        />

                    </div>
                </Flexbox>
                <Row alignItems='flex-start'>
                    <Controller
                        name={SERVICE_FORM_FIELDS.CANADA_PRICE}
                        control={control}
                        disabled={!hasCanada}
                        rules={SERVICE_FORM_RULES.CANADA_PRICE(clearErrors)}
                        render={({ field }) => {
                            return (
                                <Flexbox flexDirection='column' width='100%'>
                                    <InputLabel text={SERVICE_CONSTANTS.CANADA_PRICE} hasError={!!errors?.canadaPrice?.message} />
                                    {renderCurrencyFormatter(field, errors.canadaPrice?.message || noCountrySelected, CURRENCY_ISO.CAD, 'service-currency-format', readOnly)}
                                </Flexbox>
                            )
                        }}
                    />
                </Row>

                <Row alignItems='flex-start'>
                    <Controller
                        name={SERVICE_FORM_FIELDS.MEASUREMENT_UNIT_CA}
                        control={control}
                        disabled={!hasCanada}
                        rules={SERVICE_FORM_RULES.UNIT_OF_MEASUREMENT_CA}
                        render={({ field }) => (
                            <Flexbox flexDirection='column' width='100%'>
                                <Flexbox alignItems='center'>
                                    <InputLabel text={SERVICE_CONSTANTS.UNIT_OF_MEASUREMENT} hasError={!!errors?.measurementUnit?.message} />
                                    <StyledInfoIcon
                                        productRedesignFlag
                                        tooltipText={SERVICE_CONSTANTS.TOOLTIP_TO_BE_DETERMINED}
                                        size={14}
                                    />
                                </Flexbox>
                                <SelectComponent
                                    {...field}
                                    labelPosition="top"
                                    options={unitOfMeasurements.map((uom) => ({
                                        value: `${uom.fullName} (${uom.displayName})`,
                                        id: uom.id?.toString(),
                                    }))}
                                    onChange={(event: any) => {
                                        field.onChange(Number(event.target.id));
                                    }}
                                    value={
                                        unitOfMeasurementCa
                                            ? `${unitOfMeasurementCa.fullName} (${unitOfMeasurementCa.displayName})`
                                            : ''
                                    }
                                    defaultValue={SERVICE_CONSTANTS.SELECT_MEASUREMENT_UNIT}
                                    disabledColor={Main.disabled}
                                    readOnly={readOnly}
                                />
                            </Flexbox>
                        )}
                    />
                </Row>
            </Flexbox>

            {/* US Section */}
            <Flexbox width={'60%'} flexDirection='row' gap='10px'>
                <Flexbox flexDirection='column' width='35%'>
                    <div style={{ paddingTop: 40 }}>
                        <Controller
                            name={SERVICE_FORM_FIELDS.IS_FOR_USA}
                            control={control}
                            rules={SERVICE_FORM_RULES.IS_FOR_USA(clearErrors)}
                            render={({ field }) =>
                                renderCheckbox(field,
                                    SERVICE_FORM_FIELDS.IS_FOR_USA,
                                    GENERAL_CONSTANTS.USA,
                                    '',
                                    isCreateMode ? PermissionCode.ADDONS_CREATE : PermissionCode.EDIT_ADDON_DETAILS
                                )
                            }
                        />
                    </div>
                </Flexbox>
                <Row alignItems='flex-start'>
                    <Controller
                        name={SERVICE_FORM_FIELDS.USA_PRICE}
                        control={control}
                        rules={SERVICE_FORM_RULES.USA_PRICE(clearErrors)}
                        disabled={!hasUSA}
                        render={({ field }) => (
                            <Flexbox flexDirection='column' width='100%'>
                                <InputLabel text={SERVICE_CONSTANTS.USA_PRICE} hasError={!!errors?.usaPrice?.message} />
                                {renderCurrencyFormatter(field, errors.usaPrice?.message || noCountrySelected, CURRENCY_ISO.USD, 'service-currency-format', readOnly)}
                            </Flexbox>
                        )}
                    />
                </Row>

                <Row alignItems='flex-start'>
                    <Controller
                        name={SERVICE_FORM_FIELDS.MEASUREMENT_UNIT_USA}
                        control={control}
                        rules={SERVICE_FORM_RULES.UNIT_OF_MEASUREMENT_USA}
                        disabled={!hasUSA}
                        render={({ field }) => (
                            <Flexbox flexDirection='column' width='100%'>
                                <Flexbox alignItems='center'>
                                    <InputLabel text={SERVICE_CONSTANTS.UNIT_OF_MEASUREMENT} hasError={!!errors?.measurementUnit?.message} />
                                    <StyledInfoIcon
                                        productRedesignFlag
                                        tooltipText={SERVICE_CONSTANTS.TOOLTIP_TO_BE_DETERMINED}
                                        size={14}
                                    />
                                </Flexbox>
                                <SelectComponent
                                    {...field}
                                    labelPosition="top"
                                    options={unitOfMeasurements.map((uom) => ({
                                        value: `${uom.fullName} (${uom.displayName})`,
                                        id: uom.id?.toString(),
                                    }))}
                                    onChange={(event: any) => {
                                        field.onChange(Number(event.target.id));
                                    }}
                                    value={
                                        unitOfMeasurementUsa
                                            ? `${unitOfMeasurementUsa.fullName} (${unitOfMeasurementUsa.displayName})`
                                            : ''
                                    }
                                    defaultValue={SERVICE_CONSTANTS.SELECT_MEASUREMENT_UNIT}
                                    disabledColor={Main.disabled}
                                    readOnly={readOnly}
                                />
                            </Flexbox>
                        )}
                    />
                </Row>


            </Flexbox>
        </>
    );
}

export default ServiceCountries;